import React, { useEffect, useState, useMemo } from "react";
import Layout from "../layout.js";
import Breadcrumbs from "../../component/Breadcrumbs.js";
import linkedinIcon from "../../assets/images/linkedin.png";
import xIcon from "../../assets/images/twitter.png";
import { Link, useNavigate, useParams } from "react-router-dom";
import InsightsCard from "../../component/InsightsCard.js";
import * as Constants from "../../config/constants.js";
import { apiCall, manageNetworkError } from "../../assets/js/common.js";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import NewsletterForm from "../../component/NewsletterForm.js";

export default function ResourcesDeails({ pageName }) {
  const { type, slug } = useParams();
  const navigate = useNavigate();

  const [recordInfo, setRecordInfo] = useState({});
  const [recommendedPosts, setRecommendedPosts] = useState([]);
  const [resourcesDeailsPageName, setResourcesDeailsPageName] = useState(pageName);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const normalizedType = type?.toLowerCase();
  const isNewsletter = normalizedType === "newsletter";
  const isWhitepaper = normalizedType?.replace(/[\s-]+/g, "") === "whitepaper";

  const backUrl = isNewsletter ? "/resources/newsletter/" : "/resources/";
  const backLabel = isNewsletter ? "Back to Newsletters" : "Back to Resources";

  const getRecordDetails = async () => {
    const safeType = (type || "articles").toString().trim().toLowerCase();
    const safeSlug = (slug || "").toString().trim();

    if (!safeSlug) {
      navigate("/resources/", { replace: true });
      return;
    }

    const endpoint = `resources/${safeType}/${safeSlug}`;

    const res = await apiCall("get", endpoint, null, true, {
      isPageRequest: true,
      navigate,
    });

    // Canonical redirect handling (409)
    if (res?.status === false && res?.status_code === 409) {
      const canonicalUrl = res?.data?.data?.canonical_url;
      if (canonicalUrl) {
        navigate(canonicalUrl, { replace: true });
        return;
      }
    }

    if (res?.status !== false) {
      const payload = res?.data?.data;
      if (payload) {
        const details = payload?.contentDetails || {};
        setRecordInfo(details);
        setRecommendedPosts(payload?.recommendedPosts || []);
        setResourcesDeailsPageName(details?.content_title || pageName);
      }
    } else {
      manageNetworkError(res);
    }
  };

  useEffect(() => {
    getRecordDetails();
    // eslint-disable-next-line
  }, [type, slug]);

  /** ---------------------------------------------------
   * Canonical URL
   * -------------------------------------------------- */
  const canonicalUrl = useMemo(() => {
    const safeType = (type || "articles").toString().trim().toLowerCase();
    const safeSlug = (slug || "").toString().trim();
    return `${Constants.SITE_URL}/resources/${safeType}/${safeSlug}/`;
  }, [type, slug]);

  /** ---------------------------------------------------
   * SEO DATA (USING YOUR ACTUAL DB COLUMNS)
   * -------------------------------------------------- */
  const seoData = useMemo(() => {
    if (!recordInfo) return null;

    return {
      // ----- PRIMARY SEO FROM DB -----
      seo_title:
        recordInfo?.v_seo_title ||
        recordInfo?.content_title ||
        null,

      seo_description:
        recordInfo?.v_seo_description ||
        (recordInfo?.short_description
          ? recordInfo.short_description.replace(/<[^>]+>/g, "").slice(0, 155)
          : "Resources supporting boutique consulting firms and independent consultants with hybrid delivery model consulting and consulting execution support."),

      seo_keywords:
        recordInfo?.v_seo_keywords ||
        "scaling consulting business, boutique consulting firms, independent consultants, consulting execution partner, hybrid delivery model consulting, consulting lifecycle management, end-to-end consulting solutions, consulting delivery framework, consulting execution support",

      // ----- OG FALLBACK -----
      og_title:
        recordInfo?.v_seo_title ||
        recordInfo?.content_title ||
        null,

      og_description:
        recordInfo?.v_seo_description ||
        recordInfo?.short_description ||
        "Insights for boutique consulting firms and independent consultants on hybrid delivery model consulting and consulting execution support.",

      // ----- CANONICAL -----
      canonical_url:
        recordInfo?.canonical_url || canonicalUrl,
    };
  }, [recordInfo, canonicalUrl]);

  const handleMouseDown = (tag) => {
    localStorage.setItem(Constants.RESOURCES_KEY_NAME, JSON.stringify({ tag }));
  };

  const shareUrl = encodeURIComponent(
    seoData?.canonical_url || window?.location?.href
  );

  // Prevent thin/empty page indexing before API loads
  if (!recordInfo?.content_title) {
    return (
      <Layout pageName={pageName} seoData={null} pageUrl={canonicalUrl}>
        <div style={{ minHeight: "60vh" }} />
      </Layout>
    );
  }

  const whitepaperDetailStyle = isWhitepaper
    ? { maxWidth: "900px", margin: "0 auto", width: "100%" }
    : undefined;

  const pdfViewerWrapperStyle = isWhitepaper
    ? { height: "700px", maxWidth: "900px", margin: "0 auto", width: "100%" }
    : { height: "550px" };

  return (
    <Layout
      pageName={resourcesDeailsPageName}
      seoData={seoData}
      pageUrl={seoData?.canonical_url || canonicalUrl}
    >
      <Breadcrumbs
        title={recordInfo?.content_type}
        className="text-singleline"
        text={recordInfo?.content_title}
      />

      <section className="sec-padding border-bottom">
        <div className="container">
          <div className={`row insight-details-row ${isWhitepaper ? "whitepaper-layout" : ""}`}>
            <div className={`col-lg-${isWhitepaper ? "12" : "8"} col-left`}>
              <div className="insight-details-div" style={whitepaperDetailStyle}>
                <h5 className="title">{recordInfo?.content_title}</h5>

                <div className="info-mdiv">
                  <div className="info">
                    {(recordInfo?.content_type ||
                      recordInfo?.publish_date ||
                      recordInfo?.authors) && (
                      <div className="left-cnt">
                        {recordInfo?.content_type && (
                          <h5 className="info-text">
                            <i className="fi fi-rr-list icon"></i>
                            {recordInfo?.content_type}
                          </h5>
                        )}
                        {recordInfo?.publish_date && (
                          <h5 className="info-text">
                            <i className="fi fi-rr-calendar-day icon"></i>
                            {recordInfo?.publish_date}
                          </h5>
                        )}
                        {recordInfo?.authors && (
                          <h5 className="info-text">
                            <i className="fi fi-rr-user icon"></i>
                            {recordInfo?.authors}
                          </h5>
                        )}
                      </div>
                    )}

                    {recordInfo?.content_title && (
                      <div className="social-div">
                        <h5 className="title">Share:</h5>
                        <div className="contact-social">
                          <Link
                            to={Constants.LINKEDIN_SHARE_LINK + shareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-list-item"
                            title="Linkedin"
                          >
                            <img src={linkedinIcon} alt="Linkedin" className="icn-img" />
                          </Link>
                          <Link
                            to={Constants.X_SHARE_LINK + shareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-list-item"
                            title="X"
                          >
                            <img src={xIcon} alt="X" className="icn-img" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>

                  {Array.isArray(recordInfo?.content_tags) &&
                    recordInfo.content_tags.length > 0 && (
                      <div className="tags-mdiv">
                        <h5 className="title">Tags:</h5>
                        <div className="tags">
                          {recordInfo.content_tags.map((tag, key) => (
                            <Link
                              key={key}
                              to="/resources/"
                              onMouseDown={() => handleMouseDown(tag?.tag)}
                              onClick={() => handleMouseDown(tag?.tag)}
                              className="tag pe-auto"
                            >
                              {tag?.tag}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                </div>

                {(recordInfo?.short_description ||
                  recordInfo?.content ||
                  recordInfo?.pdf_file_path) && (
                  <div className="ck-content">
                    {recordInfo?.short_description && (
                      <p>{recordInfo.short_description}</p>
                    )}
                    {recordInfo?.content && (
                      <p dangerouslySetInnerHTML={{ __html: recordInfo.content }} />
                    )}

                    {recordInfo?.pdf_file_path && (
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <div style={pdfViewerWrapperStyle}>
                          <Viewer
                            fileUrl={`data:application/pdf;base64,${recordInfo.pdf_file_path}`}
                            plugins={[defaultLayoutPluginInstance]}
                          />
                        </div>
                      </Worker>
                    )}
                  </div>
                )}

                {isNewsletter && (
                  <>
                    <NewsletterForm title="Stay tuned for next week's consulting industry update" />
                    <hr />
                  </>
                )}
              </div>

              {recordInfo?.content_title && (
                <Link to={backUrl} className="primary-arrow-btn mt-4">
                  <i className="fi fi-rr-arrow-small-left btn-icon"></i> {backLabel}
                </Link>
              )}
            </div>

            {!isWhitepaper && (
              <div className="col-lg-4 col-right">
                <div className="recommended-post-div">
                  <h5 className="title">Recommended Posts</h5>

                  <div className="posts">
                    {recommendedPosts.length > 0 ? (
                      recommendedPosts.map((post, key) => {
                        const typeSlug =
                          post?.content_type_slug === "resources"
                            ? "articles"
                            : post?.content_type_slug;

                        const url = `/resources/${typeSlug}/${post.slug}/`;

                        return (
                          <InsightsCard
                            key={key}
                            title={post.content_title}
                            text={post.short_description}
                            date={post.publish_date}
                            category={post.content_type}
                            link={url}
                            to={url}
                          />
                        );
                      })
                    ) : (
                      <div className="text-muted">No recommended posts.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

    </Layout>
  );
}
